// ===============================================
// File: src/contexts/FeedbackContext.jsx
// Description: Sound + haptics feedback with unified persistence.
// - Uses StorageContext (Folder OR Drive) via loadJSON/saveJSON.
// - If no workspace linked => in-memory defaults; writes are no-op.
// - Restore-aware (files/vault).
// ===============================================

import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import { useStorage } from "./StorageContext";

const FeedbackContext = createContext({
  play: () => {},
  buzz: () => {},
  prefs: { soundEnabled: true, hapticsEnabled: true, volume: 0.9 },
  setSoundEnabled: () => {},
  setHapticsEnabled: () => {},
  setVolume: () => {},
});
export const useFeedback = () => useContext(FeedbackContext);

const SFX = {
  tap: "/sfx/tap.mp3",
  success: "/sfx/success.mp3",
  error: "/sfx/error.mp3",
  start: "/sfx/start.mp3",
  stop: "/sfx/stop.mp3",
  wake: "/sfx/wake.mp3",
};

const FILE_NAME = "mudoFeedbackPrefs.json";

export function FeedbackProvider({ children }) {
  const {
    hasFolder,
    isDriveLinked,
    isDriveAuthed,
    loadJSON,
    saveJSON,
  } = useStorage();

  const hasWorkspace = !!hasFolder || (!!isDriveLinked && !!isDriveAuthed);

  // Defaults; hydrate from workspace if available
  const [prefs, setPrefs] = useState({
    soundEnabled: true,
    hapticsEnabled: true,
    volume: 0.9,
  });
  const [unlocked, setUnlocked] = useState(false);

  const booted = useRef(false);
  const debounce = useRef(0);

  // ---- hydrate
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!hasWorkspace) {
        booted.current = true;
        return;
      }
      try {
        const disk = await loadJSON(FILE_NAME, null);
        if (alive && disk && typeof disk === "object") {
          setPrefs({
            soundEnabled: disk.soundEnabled !== false,
            hapticsEnabled: disk.hapticsEnabled !== false,
            volume:
              typeof disk.volume === "number"
                ? Math.max(0, Math.min(1, disk.volume))
                : 0.9,
          });
        }
      } catch {
        // keep defaults
      } finally {
        booted.current = true;
      }
    })();
    return () => { alive = false; };
  }, [hasWorkspace, loadJSON]);

  // ---- persist (debounced) when prefs change
  useEffect(() => {
    if (!booted.current || !hasWorkspace) return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        await saveJSON(FILE_NAME, prefs);
      } catch {
        // ignore write errors
      }
    }, 300);
    return () => clearTimeout(debounce.current);
  }, [prefs, hasWorkspace, saveJSON]);

  // ---- refresh on restore events
  useEffect(() => {
    const rehydrate = async () => {
      try {
        const disk = await loadJSON(FILE_NAME, null);
        if (disk && typeof disk === "object") {
          setPrefs({
            soundEnabled: disk.soundEnabled !== false,
            hapticsEnabled: disk.hapticsEnabled !== false,
            volume:
              typeof disk.volume === "number"
                ? Math.max(0, Math.min(1, disk.volume))
                : 0.9,
          });
        }
      } catch {}
    };
    window.addEventListener("mudo:files-restored", rehydrate);
    window.addEventListener("mudo:vault-restored", rehydrate);
    return () => {
      window.removeEventListener("mudo:files-restored", rehydrate);
      window.removeEventListener("mudo:vault-restored", rehydrate);
    };
  }, [loadJSON]);

  // ---- audio unlock + cache
  const cache = useMemo(() => ({}), []);
  const unlocking = useRef(false);

  useEffect(() => {
    const unlock = async () => {
      if (unlocking.current || unlocked) return;
      unlocking.current = true;
      try {
        const a = new Audio(SFX.tap);
        a.volume = 0;
        await a.play().catch(() => {});
        a.pause();
        setUnlocked(true);
      } finally {
        unlocking.current = false;
      }
    };
    const onFirstGesture = () => {
      unlock();
      window.removeEventListener("pointerdown", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
    };
    window.addEventListener("pointerdown", onFirstGesture, { once: true });
    window.addEventListener("keydown", onFirstGesture, { once: true });
    return () => {
      window.removeEventListener("pointerdown", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
    };
  }, [unlocked]);

  function play(name = "tap", volOverride) {
    if (!prefs.soundEnabled) return;
    const src = SFX[name];
    if (!src) return console.warn("[Feedback] Unknown SFX:", name);

    let audio = cache[src];
    if (!audio) {
      audio = new Audio(src);
      audio.preload = "auto";
      cache[src] = audio;
    }
    try {
      audio.currentTime = 0;
      const vol = typeof volOverride === "number" ? volOverride : 1;
      audio.volume = Math.max(0, Math.min(1, vol * (prefs.volume ?? 1)));
      const p = audio.play();
      if (p && typeof p.catch === "function") {
        p.catch((err) =>
          console.warn("[Feedback] Audio blocked/failed:", name, err?.message || err)
        );
      }
    } catch (err) {
      console.warn("[Feedback] Audio exception:", name, err?.message || err);
    }
  }

  async function buzz(style = "light") {
    if (!prefs.hapticsEnabled) return;

    const cap = window?.Capacitor;
    if (cap?.isNativePlatform) {
      try {
        const { Haptics, ImpactStyle, NotificationType } = await import(
          /* @vite-ignore */ "@capacitor/haptics"
        );
        if (style === "success")
          return Haptics.notification({ type: NotificationType.Success });
        if (style === "error")
          return Haptics.notification({ type: NotificationType.Error });
        const impact =
          style === "heavy"
            ? ImpactStyle.Heavy
            : style === "medium"
            ? ImpactStyle.Medium
            : ImpactStyle.Light;
        return Haptics.impact({ style: impact });
      } catch {
        // fall through to web vibration
      }
    }
    if (navigator?.vibrate) {
      if (style === "success") return navigator.vibrate([20, 30, 20]);
      if (style === "error") return navigator.vibrate([60, 40, 60]);
      if (style === "heavy") return navigator.vibrate(50);
      if (style === "medium") return navigator.vibrate(30);
      return navigator.vibrate(15);
    }
  }

  const setSoundEnabled = (v) => setPrefs((p) => ({ ...p, soundEnabled: !!v }));
  const setHapticsEnabled = (v) => setPrefs((p) => ({ ...p, hapticsEnabled: !!v }));
  const setVolume = (v) =>
    setPrefs((p) => ({
      ...p,
      volume: Math.max(0, Math.min(1, Number(v))),
    }));

  return (
    <FeedbackContext.Provider
      value={{
        play,
        buzz,
        prefs,
        setSoundEnabled,
        setHapticsEnabled,
        setVolume,
      }}
    >
      {children}
    </FeedbackContext.Provider>
  );
}
